/**
 * Account synchronization state machine (APP-001 §7).
 *
 * A signed-in Clerk session alone does NOT prove the local backend account is
 * ready. After Clerk resolves, the frontend calls `GET /v1/dashboard/account`:
 *
 *   200 → ACCOUNT_READY          (render the dashboard)
 *   409 ACCOUNT_SYNC_PENDING →   SYNC_PENDING (bounded retry + manual control)
 *   401/403 (auth/disabled)  →   AUTH_FAILED / ACCOUNT_DISABLED
 *   network/5xx              →   BACKEND_UNAVAILABLE (bounded retry)
 *
 * The browser never creates local accounts itself. The pending state shows a
 * dedicated "Setting up your account…" view with bounded backoff and a support
 * path after a reasonable timeout.
 */
import { useEffect, useMemo, useState } from 'react';
import { getAccountIdentity } from '@/api/dashboard';
import type { DashboardIdentityResponse } from '@/api/dashboard-types';
import { isApiError, isAuthError, isSyncPendingError, type ApiError } from '@/api/errors';
import { track } from '@/lib/analytics';
import { SynchronizationPendingView } from '@/components/feedback/SynchronizationPendingView';
import { AccountErrorView } from '@/components/feedback/AccountErrorView';
import { LoadingState } from '@/components/feedback/LoadingState';

export type AccountBootstrapStatus =
  | 'loading' // initial fetch in flight
  | 'ready' // backend account resolved
  | 'sync_pending' // 409 ACCOUNT_SYNC_PENDING
  | 'auth_failed' // 401/403 non-sync
  | 'disabled' // 403 ACCOUNT_DISABLED / USER_DISABLED
  | 'unavailable' // network / 5xx
  | 'unknown'; // unparseable / unexpected

export interface AccountBootstrapState {
  status: AccountBootstrapStatus;
  identity: DashboardIdentityResponse | null;
  error: ApiError | null;
  /** Bounded retry count (informational). */
  attempt: number;
  /** Trigger a manual retry. No-op if a request is already in flight. */
  retry: () => void;
}

const INITIAL_BACKOFF_MS = 1_500;
const MAX_BACKOFF_MS = 15_000;
const MAX_AUTO_ATTEMPTS = 8;
const BACKOFF_FACTOR = 1.6;

/**
 * Resolves the backend account for the current Clerk session. Retries
 * automatically with bounded backoff for sync-pending/unavailable states and
 * never retries auth/disabled/validation errors. Exposes a manual retry
 * control for the pending view.
 *
 * Each effect run owns exactly one request: its cleanup aborts it and the
 * `cancelled` flag discards stale results. There is deliberately NO
 * module/ref-level "in flight" guard — under React 18 StrictMode the effect
 * remounts while the first request is still settling, and such a guard would
 * swallow the second invocation, deadlocking the bootstrap on the loading
 * spinner forever.
 */
export function useAccountBootstrap(): AccountBootstrapState {
  const [status, setStatus] = useState<AccountBootstrapStatus>('loading');
  const [identity, setIdentity] = useState<DashboardIdentityResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function run() {
      setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
      try {
        const data = await getAccountIdentity(controller.signal);
        if (cancelled) return;
        setIdentity(data);
        setError(null);
        setStatus('ready');
        track({ event: 'dashboard_loaded' });
      } catch (err) {
        if (cancelled) return;
        if (!isApiError(err)) {
          setStatus('unknown');
          return;
        }
        const apiErr = err as ApiError;
        setError(apiErr);
        if (apiErr.isAborted) {
          // Ignore aborts triggered by unmount/retry.
          setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
          return;
        }
        if (isSyncPendingError(apiErr)) {
          setStatus('sync_pending');
          track({ event: 'account_sync_pending' });
        } else if (apiErr.code === 'ACCOUNT_DISABLED' || apiErr.code === 'USER_DISABLED') {
          setStatus('disabled');
        } else if (isAuthError(apiErr)) {
          setStatus('auth_failed');
        } else if (apiErr.isNetworkError || apiErr.status >= 500) {
          setStatus('unavailable');
        } else {
          setStatus('unknown');
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [retryToken]);

  // Bounded auto-retry for sync_pending / unavailable with exponential backoff.
  useEffect(() => {
    if (status !== 'sync_pending' && status !== 'unavailable') return;
    if (attempt >= MAX_AUTO_ATTEMPTS) return;
    const delay = Math.min(INITIAL_BACKOFF_MS * Math.pow(BACKOFF_FACTOR, attempt), MAX_BACKOFF_MS);
    const timer = window.setTimeout(() => {
      setAttempt((n) => n + 1);
      setRetryToken((t) => t + 1);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [status, attempt]);

  const retry = useMemo(
    () => () => {
      setAttempt(0);
      setRetryToken((t) => t + 1);
    },
    [],
  );

  return { status, identity, error, attempt, retry };
}

export interface AccountBootstrapProps {
  /** Rendered once the backend account resolves (ready). */
  children: (identity: DashboardIdentityResponse) => React.ReactNode;
}

/**
 * Renders the appropriate blocking view for each non-ready state, and calls
 * `children(identity)` once the backend account is ready. Used by
 * `ProtectedRoute` to gate the dashboard layout.
 */
export function AccountBootstrap({ children }: AccountBootstrapProps) {
  const state = useAccountBootstrap();

  if (state.status === 'ready' && state.identity) {
  // The cast preserves the non-null guarantee established above.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <>{children(state.identity)}</>;
  }
  if (state.status === 'sync_pending') {
    return <SynchronizationPendingView attempt={state.attempt} onRetry={state.retry} error={state.error} />;
  }
  if (state.status === 'disabled') {
    return <AccountErrorView kind="disabled" error={state.error} />;
  }
  if (state.status === 'auth_failed') {
    return <AccountErrorView kind="auth_failed" error={state.error} />;
  }
  if (state.status === 'unavailable') {
    return <AccountErrorView kind="unavailable" error={state.error} onRetry={state.retry} />;
  }
  if (state.status === 'unknown') {
    return <AccountErrorView kind="unknown" error={state.error} />;
  }
  return <LoadingState label="Resolving your account…" />;
}
